import { useEffect, useRef } from "react";

import posthog from "@/lib/posthog";
import { buildPostHogPersonProperties } from "@/lib/posthog-person-properties";
import { useAuth } from "@/providers/auth-provider";

/**
 * Identifies the signed-in user in PostHog and resets on logout.
 * Person properties come from `useAuth()` (Kinde user + org code/name).
 */
export const PostHogIdentity = () => {
	const { authUser, isAuthenticated, isLoadingAuth, orgCode, orgName } =
		useAuth();
	const identifiedUserId = useRef<null | string>(null);
	const identifiedFingerprint = useRef<null | string>(null);

	useEffect(() => {
		if (isLoadingAuth) {
			return;
		}

		if (!isAuthenticated || !authUser?.id) {
			if (identifiedUserId.current) {
				posthog.reset();
				identifiedUserId.current = null;
				identifiedFingerprint.current = null;
			}
			return;
		}

		const properties = buildPostHogPersonProperties({
			email: authUser.email,
			familyName: authUser.lastName,
			givenName: authUser.firstName,
			organizationId: orgCode,
			organizationName: orgName,
		});
		const fingerprint = JSON.stringify(properties);

		if (
			identifiedUserId.current === authUser.id &&
			identifiedFingerprint.current === fingerprint
		) {
			return;
		}

		if (identifiedUserId.current && identifiedUserId.current !== authUser.id) {
			posthog.reset();
		}

		posthog.identify(authUser.id, properties);
		identifiedUserId.current = authUser.id;
		identifiedFingerprint.current = fingerprint;
	}, [authUser, isAuthenticated, isLoadingAuth, orgCode, orgName]);

	return null;
};
